import { FC, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TablePagination,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
    Card,
    CardContent,
    Tooltip,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { useReports, useReportSummary, useUpdateReportStatus } from '../../hooks/useReport';
import { formatKstDateTime } from '../../utils/dateTime';
import type {
    AdminReportChoice,
    ReportStatus,
    ReportType,
    WordReportItem,
} from '../../types/report';

const STATUS_LABELS: Record<ReportStatus, string> = {
    PENDING: '대기중',
    IN_PROGRESS: '처리중',
    RESOLVED: '처리완료',
    REJECTED: '반려',
};

const STATUS_COLORS: Record<ReportStatus, 'warning' | 'info' | 'success' | 'error'> = {
    PENDING: 'warning',
    IN_PROGRESS: 'info',
    RESOLVED: 'success',
    REJECTED: 'error',
};

const VERIFY_LABELS: Record<AdminReportChoice['verify'], string> = {
    MATCH: '일치',
    TEXT_MISMATCH: '텍스트 불일치',
    NOT_FOUND: '단어 없음',
    NO_ID: 'ID 없음',
};

const VERIFY_COLORS: Record<AdminReportChoice['verify'], 'success' | 'warning' | 'error' | 'default'> = {
    MATCH: 'success',
    TEXT_MISMATCH: 'warning',
    NOT_FOUND: 'error',
    NO_ID: 'default',
};

const TYPE_LABELS: Record<ReportType, string> = {
    SPELLING_ERROR: '철자 오류',
    TRANSLATION_ERROR: '번역 오류',
    INAPPROPRIATE_WORD: '부적절한 단어',
    PRONUNCIATION_ERROR: '발음 오류',
    GRAMMAR_ERROR: '문법 오류',
    DUPLICATE: '중복 단어',
    OTHER: '기타',
};

export const WordReportList: FC = () => {
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(20);
    const [statusFilter, setStatusFilter] = useState<ReportStatus | ''>('');
    const [typeFilter, setTypeFilter] = useState<ReportType | ''>('');
    const [editDialog, setEditDialog] = useState<WordReportItem | null>(null);
    const [newStatus, setNewStatus] = useState<ReportStatus>('PENDING');
    const [adminNote, setAdminNote] = useState('');

    const { data, isLoading } = useReports({
        page,
        size,
        ...(statusFilter ? { status: statusFilter as ReportStatus } : {}),
        ...(typeFilter ? { reportType: typeFilter as ReportType } : {}),
    });
    const { data: summary } = useReportSummary();
    const updateStatus = useUpdateReportStatus();

    const reports = data?.content ?? [];
    const totalCount = data?.totalCount ?? 0;

    const handleOpenEdit = (report: WordReportItem) => {
        setEditDialog(report);
        setNewStatus(report.status);
        setAdminNote(report.adminNote ?? '');
    };

    const handleSave = () => {
        if (!editDialog) return;
        updateStatus.mutate(
            { reportId: editDialog.id, data: { status: newStatus, adminNote: adminNote || undefined } },
            { onSuccess: () => setEditDialog(null) },
        );
    };

    const formatDate = (dateStr?: string) => {
        return formatKstDateTime(dateStr);
    };

    return (
        <Box p={3}>
            <Typography variant="h4" sx={{ mb: 3 }}>신고 관리</Typography>

            {/* Summary Cards */}
            {summary && (
                <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                    <SummaryCard label="대기중" count={summary.pendingCount} color="#f59e0b" />
                    <SummaryCard label="처리중" count={summary.inProgressCount} color="#3b82f6" />
                    <SummaryCard label="처리완료" count={summary.resolvedCount} color="#22c55e" />
                    <SummaryCard label="반려" count={summary.rejectedCount} color="#ef4444" />
                </Stack>
            )}

            {/* Filters */}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>상태</InputLabel>
                    <Select
                        value={statusFilter}
                        label="상태"
                        onChange={(e) => { setStatusFilter(e.target.value as ReportStatus | ''); setPage(1); }}
                    >
                        <MenuItem value="">전체</MenuItem>
                        {(Object.keys(STATUS_LABELS) as ReportStatus[]).map((s) => (
                            <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>신고 유형</InputLabel>
                    <Select
                        value={typeFilter}
                        label="신고 유형"
                        onChange={(e) => { setTypeFilter(e.target.value as ReportType | ''); setPage(1); }}
                    >
                        <MenuItem value="">전체</MenuItem>
                        {(Object.keys(TYPE_LABELS) as ReportType[]).map((t) => (
                            <MenuItem key={t} value={t}>{TYPE_LABELS[t]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            {/* Table */}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>상태</TableCell>
                            <TableCell>신고 유형</TableCell>
                            <TableCell>단어</TableCell>
                            <TableCell>퀴즈</TableCell>
                            <TableCell>문장</TableCell>
                            <TableCell>신고 내용</TableCell>
                            <TableCell>신고자</TableCell>
                            <TableCell>신고일</TableCell>
                            <TableCell>관리자 메모</TableCell>
                            <TableCell align="center">처리</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                    로딩 중...
                                </TableCell>
                            </TableRow>
                        ) : reports.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    신고 내역이 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : (
                            reports.map((report) => (
                                <TableRow key={report.id} hover>
                                    <TableCell>{report.id}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={STATUS_LABELS[report.status] ?? report.status}
                                            color={STATUS_COLORS[report.status] ?? 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={TYPE_LABELS[report.reportType as ReportType] ?? report.reportType}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={`${report.wordText ?? ''} — ${report.meaningText ?? ''}`}>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 120 }}>
                                                    {report.wordText ?? `#${report.domainWordId}`}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 120, display: 'block' }}>
                                                    {report.meaningText ?? ''}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{report.quizType}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        {report.presentedSentenceText ? (
                                            <Tooltip title={
                                                <Box>
                                                    <Typography variant="caption" display="block">
                                                        원문: {report.presentedSentenceText}
                                                    </Typography>
                                                    <Typography variant="caption" display="block">
                                                        번역: {report.presentedSentenceMeaning ?? '-'}
                                                    </Typography>
                                                    {report.presentedAiProvider && (
                                                        <Typography variant="caption" display="block">
                                                            Provider: {report.presentedAiProvider}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 160, cursor: 'pointer' }}>
                                                    {report.presentedSentenceText}
                                                </Typography>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">-</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={report.reportText}>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                {report.reportText}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                                            {report.userNickname ?? report.userEmail ?? `#${report.userId}`}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{formatDate(report.createdAt)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                                            {report.adminNote ?? '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton size="small" onClick={() => handleOpenEdit(report)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page - 1}
                    onPageChange={(_, newPage) => setPage(newPage + 1)}
                    rowsPerPage={size}
                    onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(1); }}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="페이지당"
                />
            </TableContainer>

            {/* Edit Dialog */}
            <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>신고 처리</DialogTitle>
                <DialogContent>
                    {editDialog && (
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">단어</Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    {editDialog.wordText ?? `#${editDialog.domainWordId}`} — {editDialog.meaningText ?? ''}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">신고 유형</Typography>
                                <Typography variant="body2">
                                    {TYPE_LABELS[editDialog.reportType as ReportType] ?? editDialog.reportType}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">신고 내용</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {editDialog.reportText}
                                </Typography>
                            </Box>
                            {editDialog.presentedSentenceText && (
                                <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary">표시된 문장</Typography>
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        원문: {editDialog.presentedSentenceText}
                                    </Typography>
                                    <Typography variant="body2">
                                        번역: {editDialog.presentedSentenceMeaning ?? '-'}
                                    </Typography>
                                    {editDialog.presentedAiProvider && (
                                        <Typography variant="caption" color="text.secondary">
                                            Provider: {editDialog.presentedAiProvider}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {editDialog.presentedContext && (
                                <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            표시된 컨텍스트 스냅샷 ({editDialog.presentedContext.quizType})
                                        </Typography>
                                        <Chip label="클라 자가보고 (미검증)" color="warning" size="small" variant="outlined" />
                                        {editDialog.presentedContext.variant === 'listenToWord' && (
                                            <Chip label="듣기 문제 가능 (TTS 오디오)" size="small" />
                                        )}
                                    </Stack>
                                    {editDialog.presentedContext.questionWord && (
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            문두: {editDialog.presentedContext.questionWord}
                                        </Typography>
                                    )}
                                    <Stack spacing={0.75}>
                                        {(editDialog.presentedContext.choices ?? []).map((choice, idx) => (
                                            <Stack key={idx} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                {choice.isCorrect && (
                                                    <Chip label="정답" color="success" size="small" />
                                                )}
                                                <Typography variant="body2">{choice.text}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    (wordId: {choice.wordId ?? '-'})
                                                </Typography>
                                                <Chip
                                                    label={VERIFY_LABELS[choice.verify] ?? choice.verify}
                                                    color={VERIFY_COLORS[choice.verify] ?? 'default'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                {choice.verify === 'TEXT_MISMATCH' && choice.serverText && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        서버: {choice.serverText}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                            <FormControl size="small" fullWidth>
                                <InputLabel>상태 변경</InputLabel>
                                <Select
                                    value={newStatus}
                                    label="상태 변경"
                                    onChange={(e) => setNewStatus(e.target.value as ReportStatus)}
                                >
                                    {(Object.keys(STATUS_LABELS) as ReportStatus[]).map((s) => (
                                        <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="관리자 메모"
                                multiline
                                rows={3}
                                value={adminNote}
                                onChange={(e) => setAdminNote(e.target.value)}
                                fullWidth
                            />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialog(null)}>취소</Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={updateStatus.isPending}
                    >
                        저장
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const SummaryCard: FC<{ label: string; count: number; color: string }> = ({ label, count, color }) => (
    <Card sx={{ flex: 1, minWidth: 120 }}>
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="h5" fontWeight={700} sx={{ color }}>{count}</Typography>
        </CardContent>
    </Card>
);
